// Image compression & canvas-based AI auto-cropping utilities

/**
 * Compresses an image data URI or File to an optimal JPEG data URI.
 * Keeps resolution high (up to 1280px) while maintaining compact payload size.
 */
export async function compressImage(
  source: string | File | Blob,
  maxDimension = 1280,
  quality = 0.8
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    // Only set crossOrigin for remote HTTP URLs, not for data: URIs or blob: URIs
    if (typeof source === 'string' && (source.startsWith('http://') || source.startsWith('https://'))) {
      img.crossOrigin = 'anonymous';
    }

    const loadHandler = () => {
      try {
        let width = img.naturalWidth || img.width;
        let height = img.naturalHeight || img.height;

        if (!width || !height) {
          resolve(typeof source === 'string' ? source : '');
          return;
        }

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve(typeof source === 'string' ? source : '');
          return;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      } catch (err) {
        // Fallback to raw string if canvas processing encounters an edge case
        if (typeof source === 'string') {
          resolve(source);
        } else {
          reject(err);
        }
      }
    };

    // Attach onload and onerror handlers BEFORE setting src
    img.onload = loadHandler;
    img.onerror = () => {
      if (typeof source === 'string') {
        resolve(source);
      } else {
        reject(new Error('Failed to load image for compression.'));
      }
    };

    if (typeof source === 'string') {
      img.src = source;
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) {
          img.src = result;
        } else {
          reject(new Error('Failed to read image file.'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(source);
    }
  });
}

/**
 * Auto-crops an image using normalized bounding box [ymin, xmin, ymax, xmax] (0 to 1000)
 * provided by Google AI Vision. Adds stylish padding and studio framing.
 */
export async function autoCropGarment(
  imageSource: string,
  box2d: [number, number, number, number],
  paddingRatio = 0.08
): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    if (imageSource.startsWith('http://') || imageSource.startsWith('https://')) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      try {
        const origW = img.naturalWidth || img.width;
        const origH = img.naturalHeight || img.height;

        if (!origW || !origH) {
          resolve(imageSource);
          return;
        }

        // Extract box coordinates normalized (0-1000)
        const [yminN, xminN, ymaxN, xmaxN] = box2d;
        let ymin = (yminN / 1000) * origH;
        let xmin = (xminN / 1000) * origW;
        let ymax = (ymaxN / 1000) * origH;
        let xmax = (xmaxN / 1000) * origW;

        let boxW = xmax - xmin;
        let boxH = ymax - ymin;

        // Ensure valid box
        if (boxW <= 10 || boxH <= 10) {
          resolve(imageSource);
          return;
        }

        // Add smart breathing room / padding
        const padX = boxW * paddingRatio;
        const padY = boxH * paddingRatio;

        xmin = Math.max(0, xmin - padX);
        ymin = Math.max(0, ymin - padY);
        xmax = Math.min(origW, xmax + padX);
        ymax = Math.min(origH, ymax + padY);

        boxW = xmax - xmin;
        boxH = ymax - ymin;

        // Create cropped canvas
        const targetMax = 900;
        let targetW = boxW;
        let targetH = boxH;

        if (targetW > targetMax || targetH > targetMax) {
          if (targetW > targetH) {
            targetH = Math.round((targetH * targetMax) / targetW);
            targetW = targetMax;
          } else {
            targetW = Math.round((targetW * targetMax) / targetH);
            targetH = targetMax;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve(imageSource);
          return;
        }

        // Draw cropped slice smoothly
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, xmin, ymin, boxW, boxH, 0, 0, targetW, targetH);

        const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
        resolve(croppedDataUrl);
      } catch (err) {
        resolve(imageSource);
      }
    };

    img.onerror = () => resolve(imageSource);
    img.src = imageSource;
  });
}

/**
 * Reads a File object into a base64 Data URL
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Rotates an image Data URL 90 degrees clockwise.
 */
export async function rotateImageData90(sourceDataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    if (sourceDataUrl.startsWith('http://') || sourceDataUrl.startsWith('https://')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalHeight || img.height;
        canvas.height = img.naturalWidth || img.width;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(sourceDataUrl);

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((90 * Math.PI) / 180);
        ctx.drawImage(img, -canvas.height / 2, -canvas.width / 2);

        const isPng = sourceDataUrl.startsWith('data:image/png');
        resolve(canvas.toDataURL(isPng ? 'image/png' : 'image/jpeg', 0.88));
      } catch (err) {
        resolve(sourceDataUrl);
      }
    };
    img.onerror = () => resolve(sourceDataUrl);
    img.src = sourceDataUrl;
  });
}

/**
 * Captures a center-cropped portrait frame (target aspect ratio default 3:4)
 * from a live HTMLVideoElement, regardless of whether camera hardware outputs landscape 16:9.
 */
export function cropVideoToPortrait(
  video: HTMLVideoElement,
  targetAspect = 3 / 4,
  quality = 0.88
): string {
  const vWidth = video.videoWidth || 1080;
  const vHeight = video.videoHeight || 1920;

  let sx = 0;
  let sy = 0;
  let sWidth = vWidth;
  let sHeight = vHeight;

  if (vWidth / vHeight > targetAspect) {
    // Stream is wider than target portrait aspect (e.g. 16:9 landscape) -> crop sides
    sWidth = Math.round(vHeight * targetAspect);
    sx = Math.round((vWidth - sWidth) / 2);
  } else {
    // Stream is taller than target portrait aspect -> crop top/bottom
    sHeight = Math.round(vWidth / targetAspect);
    sy = Math.round((vHeight - sHeight) / 2);
  }

  const canvas = document.createElement('canvas');
  canvas.width = sWidth;
  canvas.height = sHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, sWidth, sHeight);

  return canvas.toDataURL('image/jpeg', quality);
}

/**
 * Isolates a garment by auto-cropping to bounding box and removing the background
 * (surface, table, wall, bed) by sampling border colors and applying an alpha feather mask.
 * Returns a transparent PNG Data URL ready for realistic on-body virtual fitting.
 */
export async function createGarmentCutout(
  imageSource: string,
  box2d?: [number, number, number, number],
  bgColorHint?: string
): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    if (imageSource.startsWith('http://') || imageSource.startsWith('https://')) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      try {
        const origW = img.naturalWidth || img.width;
        const origH = img.naturalHeight || img.height;
        if (!origW || !origH) return resolve(imageSource);

        let sx = 0;
        let sy = 0;
        let sw = origW;
        let sh = origH;

        if (box2d && box2d.length === 4) {
          const [yminN, xminN, ymaxN, xmaxN] = box2d;
          sx = Math.max(0, (xminN / 1000) * origW);
          sy = Math.max(0, (yminN / 1000) * origH);
          sw = Math.min(origW - sx, ((xmaxN - xminN) / 1000) * origW);
          sh = Math.min(origH - sy, ((ymaxN - yminN) / 1000) * origH);
        }

        // Limit size for optimal canvas performance
        const maxD = 700;
        let dw = sw;
        let dh = sh;
        if (dw > maxD || dh > maxD) {
          if (dw > dh) {
            dh = Math.round((dh * maxD) / dw);
            dw = maxD;
          } else {
            dw = Math.round((dw * maxD) / dh);
            dh = maxD;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = dw;
        canvas.height = dh;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(imageSource);

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, dw, dh);

        const imgData = ctx.getImageData(0, 0, dw, dh);
        const data = imgData.data;

        // Sample background color candidates from perimeter and corners
        const cornerSamples: [number, number, number][] = [];
        const sampleCoords = [
          [2, 2],
          [dw - 3, 2],
          [2, dh - 3],
          [dw - 3, dh - 3],
          [Math.floor(dw / 2), 2],
          [Math.floor(dw / 2), dh - 3],
          [2, Math.floor(dh / 2)],
          [dw - 3, Math.floor(dh / 2)],
        ];

        for (const [cx, cy] of sampleCoords) {
          if (cx >= 0 && cx < dw && cy >= 0 && cy < dh) {
            const idx = (cy * dw + cx) * 4;
            cornerSamples.push([data[idx], data[idx + 1], data[idx + 2]]);
          }
        }

        if (bgColorHint && bgColorHint.startsWith('#') && bgColorHint.length >= 7) {
          const hr = parseInt(bgColorHint.slice(1, 3), 16);
          const hg = parseInt(bgColorHint.slice(3, 5), 16);
          const hb = parseInt(bgColorHint.slice(5, 7), 16);
          if (!isNaN(hr) && !isNaN(hg) && !isNaN(hb)) {
            cornerSamples.push([hr, hg, hb]);
          }
        }

        // Color distance thresholds for background transparentizing
        const tolerance = 40;
        const feather = 20;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          let minDist = 999999;
          for (const [br, bg, bb] of cornerSamples) {
            const dist = Math.hypot(r - br, g - bg, b - bb);
            if (dist < minDist) minDist = dist;
          }

          if (minDist <= tolerance) {
            data[i + 3] = 0;
          } else if (minDist < tolerance + feather) {
            const fraction = (minDist - tolerance) / feather;
            data[i + 3] = Math.round(data[i + 3] * fraction);
          }
        }

        ctx.putImageData(imgData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        resolve(imageSource);
      }
    };

    img.onerror = () => resolve(imageSource);
    img.src = imageSource;
  });
}

